/**
 * AnnouncementPhotoConfig
 *
 * Configure an optional announcement image shown in the
 * slideshow. Shows current image, allows choosing/changing/removing.
 */
import React from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Paper,
  Button,
  Box,
} from "@mui/material";
import PhotoIcon from "@mui/icons-material/Photo";
import InfoIcon from "@mui/icons-material/Info";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// Import slide configuration for timing
import { SLIDE_DURATIONS, SLIDE_TYPES } from "../Slides/slideConfig";

/**
 * AnnouncementPhotoConfig Component
 *
 * Handles the configuration of announcement photos for the kiosk slideshow.
 *
 * @param {Object} props - Component props
 * @param {string} props.announcementPhotoUrl - Current photo URL
 * @param {File|null} props.pendingImageFile - Pending image file for upload
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onFileSelect - File selection handler
 * @param {Function} props.onPreviewPhoto - Photo preview handler
 * @param {Function} props.onRemovePhoto - Photo removal handler
 *
 * @component
 */
export default function AnnouncementPhotoConfig({
  announcementPhotoUrl,
  pendingImageFile,
  loading,
  onFileSelect,
  onPreviewPhoto,
  onRemovePhoto,
}) {
  const announcementDuration = SLIDE_DURATIONS[SLIDE_TYPES.ANNOUNCEMENT] / 1000; // Convert to seconds

  const getStatusColor = () => {
    if (pendingImageFile) return "info";
    if (announcementPhotoUrl) return "success";
    return "warning";
  };

  const getStatusLabel = () => {
    if (pendingImageFile) return "Pending Upload";
    if (announcementPhotoUrl) return "Configured";
    return "Not Configured";
  };

  const getDescription = () => {
    if (announcementPhotoUrl) {
      return "An announcement image is configured and will be displayed in the kiosk slideshow. The image will automatically scale to fit the available display space while maintaining aspect ratio.";
    }
    if (pendingImageFile) {
      return "";
    }
    return "Upload an image file for announcements that will be displayed in the kiosk slideshow. The image will automatically scale to fit the available display space while maintaining aspect ratio. Recommended: JPEG or PNG format, 2521×1418 pixels (16:9 aspect ratio), 80 DPI resolution for optimal display quality.";
  };

  return (
    <Card
      elevation={8}
      sx={{
        border: "2px solid",
        borderColor: getStatusColor() + ".main",
        borderRadius: 3,
        "&:hover": {
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <PhotoIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6">Announcement Photo (Optional)</Typography>
          <Chip
            label={getStatusLabel()}
            color={getStatusColor()}
            size="small"
          />
          <Chip
            icon={<AccessTimeIcon />}
            label={`${announcementDuration}s`}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getDescription()}
        </Typography>

        {/* Pending File Indicator */}
        {pendingImageFile && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "info.50",
              border: "1px solid",
              borderColor: "info.200",
              borderRadius: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {/* Preview Box */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "grey.300",
                  backgroundColor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={URL.createObjectURL(pendingImageFile)}
                  alt="Announcement Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              </Box>
              {/* File Info */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InfoIcon color="info" sx={{ fontSize: 20 }} />
                  <Typography variant="body2" color="info.dark">
                    <strong>Selected file:</strong> {pendingImageFile.name}(
                    {Math.round(pendingImageFile.size / 1024)} KB)
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="info.dark"
                  sx={{ mt: 1, display: "block" }}
                >
                  This file will be uploaded when you click "Save Slideshow
                  Configuration"
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {/* Image Preview Box for already uploaded image */}
        {announcementPhotoUrl && !pendingImageFile && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {/* Preview Box */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "grey.300",
                  backgroundColor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={announcementPhotoUrl}
                  alt="Announcement Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              </Box>
              {/* Text Info */}
              <Box>
                <Typography variant="body2" color="success.dark">
                  <strong>Current image:</strong> Configured and active
                </Typography>
                <Typography
                  variant="caption"
                  color="success.dark"
                  sx={{ mt: 1, display: "block" }}
                >
                  This image is currently displayed in the kiosk slideshow
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Hidden file input for choosing photo */}
        <input
          id="announcement-photo-input"
          type="file"
          accept="image/*"
          onChange={onFileSelect}
          style={{ display: "none" }}
        />

        {/* Action Buttons - always in the same row */}
        <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 1 }}>
          {/* Change Photo / Change Selected File / Choose Image File */}
          <label htmlFor="announcement-photo-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={loading}
              size="small"
            >
              {loading
                ? "Saving..."
                : announcementPhotoUrl
                ? "Change Photo"
                : pendingImageFile
                ? "Change Selection"
                : "Choose Image File"}
            </Button>
          </label>

          {/* View Full Photo */}
          {announcementPhotoUrl && !pendingImageFile && (
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={onPreviewPhoto}
              size="small"
            >
              View Full Photo
            </Button>
          )}

          {/* Remove Photo or Remove Selection */}
          {(announcementPhotoUrl || pendingImageFile) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onRemovePhoto}
              size="small"
            >
              {announcementPhotoUrl && !pendingImageFile
                ? "Remove Photo"
                : "Remove Selection"}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
